import convertLetterSpacingToEm from '../../../convertLetterSpacingToEm';
import uid from '../uid';
import { degreesToRadians } from '../converter';
import getLettersRotations from '../getLettersRotations';
import { TEXT_TRANSFORM_DEFAULTS } from './constants';

/**
 * Parses a given text to path options
 */
const parseOptions = ({ fontSize = '32', letterSpacing = '0', curvatureAngle = '0' }) => {
    const fontSizeFloat = parseFloat(fontSize);
    const letterSpacingFloat = convertLetterSpacingToEm(letterSpacing, fontSizeFloat);
    const curvatureAngelInt = parseInt(curvatureAngle, 10);

    return {
        ...TEXT_TRANSFORM_DEFAULTS,
        fontSize: fontSizeFloat,
        letterSpacing: letterSpacingFloat,
        curvatureAngle: curvatureAngelInt,
    };
};

/**
 * Iterates over text letters in order to measure their width
 * @returns {[Number]} - Array of numbers representing the width of each letter
 */
const getLettersWidth = (transformer, text, { fontSize, letterSpacing, tracking, kerning }) => {
    const fontScale = (1 / transformer.font.unitsPerEm) * fontSize;

    const glyphs = transformer.font.stringToGlyphs(text, {
        kerning,
        letterSpacing,
        tracking,
    });

    return glyphs.map((glyph, i) => {
        let letterWidth = 0;

        if (glyph.advanceWidth) {
            letterWidth += glyph.advanceWidth * fontScale;
        }

        if (kerning && i < glyphs.length - 1) {
            const kerningValue = transformer.font.getKerningValue(glyph, glyphs[i + 1]);
            letterWidth += kerningValue * fontScale;
        }

        if (letterSpacing) {
            letterWidth += letterSpacing * fontSize;
        }

        return letterWidth;
    });
};

/**
 * Returns the curve coordinates of a given angel and a chord ( total width )
 * @param {Number} angle - The curve angel ( -360 to 360 )
 * @param {Number} direction - The curve direction ( 1 / -1 )
 * @param {Number} totalWidth - The chord width
 * @param {Number} lineHeight - The chord height
 * @param {String[]} letters - The text letters array
 * @param {Number[]} lettersWidth - The text letters calculated width
 * @returns {Object[]} Array contains the x/y and the rotation of an element in the curve
 */
const getCurveCords = (angle, { direction, totalWidth, fontSize: lineHeight, letters, lettersWidth }) => {
    // Calculating curve radius, based on https://github.com/peterhry/CircleType/blob/master/src/class.js#L57
    const curveRadius = totalWidth / degreesToRadians(angle) + lineHeight;

    // The actual curve radius will be the inner one ( it's the letters top left point )
    const innerRadius = curveRadius - lineHeight;

    const originY = direction === -1 ? -1 * curveRadius - lineHeight * 0.5 : curveRadius;

    const { total: totalRotation, rotations } = getLettersRotations(lettersWidth, innerRadius);

    return letters.map((letter, index) => {
        const rotate = (totalRotation * -0.5 + rotations[index]) * direction;
        const translateX = lettersWidth[index] * -0.5;

        return {
            rotate,
            x: translateX,
            y: originY,
        };
    });
};

/**
 * Main methods used to curve a text using the following:
 * 1) Split text into letters, Measure their individual width.
 * 2) Calculate curve cords by angel and direction
 * 3) Turn each letter into drawable d path attributes
 * @param {Object} transformer - TextToSVG transformer.
 * @param {Object} metrics - The text to transform into path
 * @param {String}text - The text to transform into path
 * @param {Object} options - Transforming options
 */
const getLettersPath = (transformer, metrics, text, options) => {
    const { fontSize, curvatureAngle, letterSpacing, kerning, tracking } = options;

    const lettersWidth = getLettersWidth(transformer, text, { fontSize, letterSpacing, tracking, kerning });
    const direction = curvatureAngle > 0 ? -1 : 1;

    const curveCords = getCurveCords(Math.abs(curvatureAngle), {
        fontSize,
        lettersWidth,
        direction,
        totalWidth: metrics.width,
        letters: text.split(''),
    });

    const allPaths = [];
    let firstgX;
    transformer.font.forEachGlyph(
        text,
        metrics.x,
        metrics.y,
        fontSize,
        { kerning, tracking, letterSpacing },
        (glyph, gX, gY, gFontSize) => {
            firstgX = firstgX !== undefined ? gX : firstgX;

            const glyphPath = glyph.getPath(firstgX, gY, gFontSize, options, this);
            allPaths.push(glyphPath);
        }
    );

    return { allPaths, curveCords };
};

/**
 * Returns a paths array representation of a given text
 * @param {Object} transformer - TextToSVG transformer.
 * @param {String} text - The text to transform into path
 * @param {Object} options - Transforming options
 * @returns {DrawablePath[]}
 */
const textToPath = (transformer, text, options) => {
    const parsedOptions = parseOptions(options);
    const metrics = transformer.getMetrics(text, parsedOptions);

    if (!parsedOptions.curvatureAngle || parsedOptions.curvatureAngle === 0) {
        return [{ id: uid(), d: transformer.getD(text, parsedOptions), transform: 'translate(0, 0)' }];
    }

    const letters = getLettersPath(transformer, metrics, text, parsedOptions);

    return letters.allPaths.map((pathItem, index) => {
        const cords = letters.curveCords[index];

        const { x1: left, x2: right } = pathItem.getBoundingBox();
        const center = left + Math.abs(left - right) / 2;

        return {
            id: uid(),
            d: pathItem.toPathData(),
            transform: `translate(${cords.x} 0), rotate(${cords.rotate} ${center} ${cords.y})`,
        };
    });
};

export default textToPath;
